import { useIntl } from 'react-intl'
import { connect } from 'react-redux'
import { useNavigate } from 'react-router-dom'

import { Icons } from '@pijma/crypto'

import { EPaymentMethod } from '@api/types'
import { useToken } from '@hooks'
import { Dispatch, RootState } from '@stores'
import { ECurrency, isoToECurrency } from '@stores/types/*'
import {
  extractCurrencySymbol,
  extractMaxLimit,
  extractMinLimit,
  localeNumber,
} from '@utils'
import styled from 'styled-components'

import { DarkBgClass } from '../../../../../generated/quicktype/Tokens'
import { appClose } from '../../../../helpers/route-utils'
import { isWebview } from '../../../../helpers/webview-utils'
import { SESSION_GW_KEY } from '../../../../stores/payform/payForm'
import { EPaymentProvider } from '../../../../stores/types/EPaymentProvider'
import { EProduct } from '../../../../stores/types/EProduct'
import { TPaymentGateway } from '../../../../stores/types/TPaymentGatewayState'
import { Island } from '../../../common/island'
import { FlexBoxCenteredLayout } from '../../../common/layout/flex-box'
import { BodyText600, CaptionText500 } from '../../../common/text/BodyText'
import { ExchangeRate } from './ExchangeRate'

const Container = styled.div<DarkBgClass>`
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 100%;
  color: ${(bgClass) => bgClass.primary.default.value};
`

const ProviderBlock = styled.div`
  display: flex;
  width: 100%;
  padding: 0 20px 0;
  margin-bottom: 16px;
  * {
    cursor: pointer;
  }
`

const ProviderDescription = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  align-self: stretch;
  gap: 4px;
  flex: 1 0 0;
  margin: 0 16px 16px 16px;
  width: auto;
`

const HeaderTextContainer = styled.div<DarkBgClass>`
  display: flex;
  width: 100%;
  justify-content: space-between;
  color: ${(bgClass) => bgClass.secondary.default.value};
  margin: 0 0 16px;
  padding: 0 24px;
`

const TextContainer = styled.div`
  display: flex;
  width: 100%;
  justify-content: space-between;
`

const LabelText = styled.div`
  color: #868686;
`

const CloseContainer = styled.div`
  margin: 8px 20px 0 20px;
  color: #217aac;

  span {
    cursor: pointer;

    :hover {
      color: rgb(49, 49, 49);
    }
  }
`

const AnotherOptionsContent = styled.div`
  display: flex;
  flex-direction: column;
  padding: 16px 80px 16px 16px;
  gap: 2px;
  cursor: pointer;
  position: relative;
`

interface StateProps {
  product?: EProduct
  paymentGateways: TPaymentGateway[]
}

type Props = StateProps & DispatchProps

interface DispatchProps {
  setRate: (rate: number | undefined) => void
  setLimits: (minLimit: string, maxLimit: string) => void
  setPaymentGateway: (paymentGateway: TPaymentGateway) => void
  setInrPaymentGateways: (inrPaymentGateways: TPaymentGateway[]) => void
}

export const PurchaseProviderSelectPage = (props: Props) => {
  const intl = useIntl()
  const redirectTo = useNavigate()

  const goToSourceSelectGw = (gw: TPaymentGateway) => {
    props.setPaymentGateway(gw)
    sessionStorage.setItem(SESSION_GW_KEY, gw.paymentGatewayId)

    props.setRate(gw.exchangeRate)
    props.setLimits(extractMinLimit(gw), extractMaxLimit(gw))

    if (
      gw.provider === EPaymentProvider.SPAYZ &&
      isoToECurrency(gw.currencyFrom) === ECurrency.INR
    ) {
      const inrPaymentGateways = props.paymentGateways.filter(
        (item: TPaymentGateway) =>
          isoToECurrency(item.currencyFrom) === ECurrency.INR,
      )
      props.setInrPaymentGateways(inrPaymentGateways)

      redirectTo(`../sum`)
      return
    }

    redirectTo(`../source/${gw.paymentMethod}`)
  }

  const tokens = useToken()

  const mapPaymentMethodToText = (
    method: EPaymentMethod,
    currencyFrom: ECurrency | null,
  ): string => {
    if (method === EPaymentMethod.SBP && currencyFrom === ECurrency.RUB) {
      return intl.formatMessage({ id: 'purchaseProviderSelectPage.sbp' })
    } else if (method === EPaymentMethod.BANK_CARD) {
      switch (currencyFrom) {
        case ECurrency.USD:
          return intl.formatMessage({ id: 'purchaseProviderSelectPage.visa' })
        case ECurrency.UZS:
          return intl.formatMessage({ id: 'purchaseProviderSelectPage.uz' })
        default:
          throw new Error(
            `unexpected currencyFrom ${currencyFrom} for EPaymentMethod ${method}`,
          )
      }
    } else if (currencyFrom === ECurrency.THB) {
      return intl.formatMessage({ id: 'purchaseProviderSelectPage.thaiQr' })
    } else if (currencyFrom === ECurrency.INR) {
      return intl.formatMessage({ id: 'purchaseProviderSelectPage.upiImps' })
    } else {
      return method
    }
  }

  const filteredPaymentGateways = [
    ...new Map(
      props.paymentGateways.map((item) => [item.currencyFrom, item]),
    ).values(),
  ]

  return (
    <FlexBoxCenteredLayout>
      <Container {...tokens.color.font.light_bg}>
        <HeaderTextContainer {...tokens.color.font.light_bg}>
          <CaptionText500>
            {intl.formatMessage({ id: 'purchaseProviderSelectPage.title' })}
          </CaptionText500>
          <CaptionText500>
            {intl.formatMessage({ id: 'purchaseProviderSelectPage.rate' })}
          </CaptionText500>
        </HeaderTextContainer>
        {filteredPaymentGateways.map((gw) => (
          <ProviderBlock
            key={gw.paymentGatewayId}
            onClick={() => goToSourceSelectGw(gw)}
            data-testid="provider"
          >
            <Island size="M" bgColor="primary">
              <ExchangeRate paymentGateway={gw}></ExchangeRate>
              <ProviderDescription>
                <TextContainer data-testid="provider.limits">
                  <LabelText>
                    <CaptionText500>
                      {intl.formatMessage({
                        id: 'purchaseProviderSelectPage.limit',
                      })}
                    </CaptionText500>
                  </LabelText>
                  <CaptionText500>
                    {localeNumber(extractMinLimit(gw))}
                    {' – '}
                    {localeNumber(extractMaxLimit(gw))}{' '}
                    {extractCurrencySymbol(gw.currencyFrom)}
                  </CaptionText500>
                </TextContainer>
                <TextContainer data-testid="provider.payment-methods">
                  <LabelText>
                    <CaptionText500>
                      {intl.formatMessage({
                        id: 'purchaseProviderSelectPage.payment',
                      })}
                    </CaptionText500>
                  </LabelText>
                  <CaptionText500>
                    {mapPaymentMethodToText(
                      gw.paymentMethod,
                      isoToECurrency(gw.currencyFrom),
                    )}
                  </CaptionText500>
                </TextContainer>
              </ProviderDescription>
            </Island>
          </ProviderBlock>
        ))}
        <ProviderBlock>
          <Island
            size="M"
            bgColor="primary"
            onPress={() => redirectTo('../additional-methods')}
          >
            <AnotherOptionsContent>
              <BodyText600>
                {intl.formatMessage({
                  id: 'purchaseProviderSelectPage.otherMethods',
                })}
              </BodyText600>

              <CaptionText500 color="#868686">
                {intl.formatMessage({
                  id: 'purchaseProviderSelectPage.otherMethodsDescription',
                })}
              </CaptionText500>

              <Icons.IconArrowRight
                style={{ position: 'absolute', right: 11, top: 16 }}
                color="#8C8C8C"
              />
            </AnotherOptionsContent>
          </Island>
        </ProviderBlock>

        {!isWebview(navigator.userAgent) && (
          <CloseContainer onClick={() => appClose()}>
            <BodyText600>
              {props.product
                ? intl.formatMessage(
                    { id: 'purchaseProviderSelectPage.back' },
                    { product: props.product },
                  )
                : null}
            </BodyText600>
          </CloseContainer>
        )}
      </Container>
    </FlexBoxCenteredLayout>
  )
}

const mapStateToProps = (state: RootState): StateProps => ({
  product: state.payForm?.application?.product,
  paymentGateways: state.paymentGateways?.paymentGateways ?? [],
})

const mapDispatchToProps = (dispatch: Dispatch): DispatchProps => {
  return {
    setRate: (rate: number | undefined) =>
      dispatch.payForm.set({
        rate,
      }),
    setLimits: (minLimit: string, maxLimit: string) => {
      dispatch.payForm.set({
        minLimit,
        maxLimit,
      })
    },
    setPaymentGateway: (paymentGateway: TPaymentGateway) => {
      dispatch.payForm.set({
        paymentGateway,
      })
    },
    setInrPaymentGateways: (inrPaymentGateways: TPaymentGateway[]) => {
      dispatch.payForm.set({
        inrPaymentGateways,
      })
    },
  }
}

export default connect(
  mapStateToProps,
  mapDispatchToProps,
)(PurchaseProviderSelectPage)
